/**
 * Build placement preview — mirrors useGridActions placement rules without mutating state.
 */
import { BuildingType } from '../types';
import type { Grid } from '../types';
import { BUILDINGS } from '../constants';
import { calculateConnectivity } from '../src/domain/services/connectivity';

export type BuildPreviewResult = {
  buildingName: string;
  cost: number;
  /** Immediate budget change on place (negative = spend). */
  budgetDelta: number;
  /** Per-tick budget generation after place. */
  budgetGenPerTick: number;
  /** Per-tick compliance change after place. */
  complianceDelta: number;
  /** Per-tick threat change after place. */
  threatDelta: number;
  willConnect: boolean;
  isValid: boolean;
  errors: string[];
};

export function computeBuildPreview(
  grid: Grid,
  x: number,
  y: number,
  buildingType: BuildingType,
  budget: number
): BuildPreviewResult | null {
  if (buildingType === BuildingType.None) return null;

  const tile = grid[y]?.[x];
  if (!tile || tile.buildingType !== BuildingType.None) return null;

  const config = BUILDINGS[buildingType];
  const errors: string[] = [];

  if (budget < config.cost) {
    errors.push(`Insufficient funds (need $${config.cost.toLocaleString()})`);
  }

  const simulatedGrid = grid.map((row) => row.map((t) => ({ ...t })));
  simulatedGrid[y][x] = {
    ...tile,
    buildingType,
    level: 1,
    isConnected: false,
  };

  const connectedGrid = calculateConnectivity(simulatedGrid);
  const willConnect = connectedGrid[y][x].isConnected ?? false;

  return {
    buildingName: config.name,
    cost: config.cost,
    budgetDelta: -config.cost,
    budgetGenPerTick: config.budgetGen,
    complianceDelta: config.complianceGen,
    threatDelta: config.threatGen,
    willConnect,
    isValid: errors.length === 0,
    errors,
  };
}